// src/components/lesson/PathBar.tsx
//
// "Step 3 of 20 · Algebra" on a lesson page, with the previous and next steps
// along the learning path. A sub-topic lesson (a module) shows the step it
// belongs to. Position and neighbours come from the pure path model, so the
// bar renders in the static HTML; the Passed/In-progress badge is the one
// personal bit and arrives with the client-side progress hydration.

'use client'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, Lock } from 'lucide-react'
import { MasteryMedal } from '@/components/MasteryMedal'
import { adjacentSteps, findStepBySlug, stepPosition } from '@/lib/learn/learning-path'
import { PATH_STATE_LABEL, type PathStepProgress } from '@/lib/learn/path-progress'

export default function PathBar({
  slug,
  progress,
  variant = 'top',
}: {
  /** The lesson's slug — a step's own lesson or one of its modules. */
  slug: string
  /** This step's progress once hydrated; undefined/null renders the static bar. */
  progress?: PathStepProgress | null
  variant?: 'top' | 'bottom'
}) {
  const step = findStepBySlug(slug)
  if (!step) return null
  const pos = stepPosition(step.topic)!
  const { prev, next } = adjacentSteps(step.topic)
  const isModule = step.lessonSlug !== slug

  if (variant === 'bottom') {
    return (
      <nav aria-label="Learning path" className="mt-8 grid gap-3 sm:grid-cols-2">
        {prev ? (
          <Link
            href={`/learn/${prev.lessonSlug}`}
            className="group flex items-center gap-3 rounded-lg border border-border bg-card/60 p-4 transition hover:border-brand/50 hover:bg-card"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:-translate-x-0.5" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Previous step
              </div>
              <div className="truncate text-sm font-semibold text-foreground">{prev.topic}</div>
            </div>
          </Link>
        ) : (
          <span />
        )}
        {next && (
          <Link
            href={`/learn/${next.lessonSlug}`}
            className="group flex items-center justify-end gap-3 rounded-lg border border-brand/30 bg-brand/10 p-4 text-right transition hover:border-brand/60 hover:bg-brand/20"
          >
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-brand-ink/70">Next step</div>
              <div className="truncate text-sm font-semibold text-brand-ink">{next.topic}</div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-brand-ink transition group-hover:translate-x-0.5" />
          </Link>
        )}
      </nav>
    )
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card/60 px-3 py-2 text-xs">
      <span className="font-semibold text-foreground">
        {isModule ? `Part of step ${pos.n} of ${pos.of}` : `Step ${pos.n} of ${pos.of}`}
      </span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">{step.domain}</span>
      {isModule && (
        <>
          <span className="text-muted-foreground">·</span>
          <Link href={`/learn/${step.lessonSlug}`} className="text-brand-ink hover:underline">
            {step.topic}
          </Link>
        </>
      )}
      {progress?.state === 'locked' && (
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-muted-foreground">
          <Lock className="h-3 w-3" aria-hidden="true" />
          {PATH_STATE_LABEL.locked}
        </span>
      )}
      {progress && progress.state !== 'available' && progress.state !== 'locked' && (
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-foreground">
          <MasteryMedal tier={progress.tier} className="h-4 w-4" />
          {PATH_STATE_LABEL[progress.state]}
          {progress.accuracyPct !== null && (
            <span className="text-muted-foreground">
              · {progress.accuracyPct}% over {progress.attempted}
            </span>
          )}
        </span>
      )}
      <span className="ml-auto flex items-center gap-3">
        {prev && (
          <Link
            href={`/learn/${prev.lessonSlug}`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-brand-ink"
            title={prev.topic}
          >
            <ArrowLeft className="h-3 w-3" /> Prev
          </Link>
        )}
        {next && (
          <Link
            href={`/learn/${next.lessonSlug}`}
            className="inline-flex items-center gap-1 text-brand-ink hover:underline"
            title={next.topic}
          >
            Next <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </span>
    </div>
  )
}
